
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { FoodItem } from '@/components/food-search/types';

interface CustomFoodData {
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  iron?: number;
  calcium?: number;
  zinc?: number;
  magnesium?: number;
  omega3?: number;
}

export const useCustomFoods = () => {
  const [customFoods, setCustomFoods] = useState<FoodItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Cargar alimentos personalizados del usuario
  const loadCustomFoods = async () => {
    try {
      setIsLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No user authenticated for custom foods');
        return;
      }

      const { data, error } = await supabase
        .from('user_custom_foods')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading custom foods:', error);
        toast.error('Error al cargar alimentos personalizados');
        return;
      }

      if (data) {
        const foods: FoodItem[] = data.map(item => ({
          id: `custom-${item.id}`,
          name: item.name,
          calories: Number(item.calories),
          protein: Number(item.protein),
          carbs: Number(item.carbs),
          fat: Number(item.fat),
          iron: item.iron ? Number(item.iron) : undefined,
          calcium: item.calcium ? Number(item.calcium) : undefined,
          zinc: item.zinc ? Number(item.zinc) : undefined,
          magnesium: item.magnesium ? Number(item.magnesium) : undefined,
          omega3: item.omega3 ? Number(item.omega3) : undefined,
          source: item.source || 'Personalizado'
        }));

        setCustomFoods(foods);
        console.log(`Loaded ${foods.length} custom foods`);
      }
    } catch (error) {
      console.error('Error in loadCustomFoods:', error);
      toast.error('Error al cargar alimentos personalizados');
    } finally {
      setIsLoading(false);
    }
  };

  // Guardar nuevo alimento personalizado
  const saveCustomFood = async (foodData: CustomFoodData): Promise<FoodItem | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Debes estar logueado para guardar alimentos personalizados');
        return null;
      }

      const { data, error } = await supabase
        .from('user_custom_foods')
        .insert({
          user_id: user.id,
          name: foodData.name,
          calories: foodData.calories,
          protein: foodData.protein,
          carbs: foodData.carbs,
          fat: foodData.fat,
          iron: foodData.iron || 0,
          calcium: foodData.calcium || 0,
          zinc: foodData.zinc || 0,
          magnesium: foodData.magnesium || 0,
          omega3: foodData.omega3 || 0,
          source: 'Personalizado'
        })
        .select()
        .single();

      if (error) {
        console.error('Error saving custom food:', error);
        toast.error('Error al guardar el alimento personalizado');
        return null;
      }

      if (data) {
        const newFood: FoodItem = {
          id: `custom-${data.id}`,
          name: data.name,
          calories: Number(data.calories),
          protein: Number(data.protein),
          carbs: Number(data.carbs),
          fat: Number(data.fat),
          iron: data.iron ? Number(data.iron) : undefined,
          calcium: data.calcium ? Number(data.calcium) : undefined,
          zinc: data.zinc ? Number(data.zinc) : undefined,
          magnesium: data.magnesium ? Number(data.magnesium) : undefined,
          omega3: data.omega3 ? Number(data.omega3) : undefined,
          source: 'Personalizado'
        };

        // Actualizar lista local
        setCustomFoods(prev => [newFood, ...prev]);
        
        toast.success(`Alimento personalizado "${newFood.name}" guardado correctamente`);
        return newFood;
      }
    } catch (error) {
      console.error('Error in saveCustomFood:', error);
      toast.error('Error al guardar el alimento personalizado');
    }
    
    return null;
  };

  // Eliminar alimento personalizado
  const deleteCustomFood = async (foodId: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Debes estar logueado para eliminar alimentos personalizados');
        return false;
      }

      // Extraer el ID real del formato 'custom-{uuid}'
      const realId = foodId.replace('custom-', '');

      const { error } = await supabase
        .from('user_custom_foods')
        .delete()
        .eq('id', realId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting custom food:', error);
        toast.error('Error al eliminar el alimento personalizado');
        return false;
      }

      // Actualizar lista local
      setCustomFoods(prev => prev.filter(food => food.id !== foodId));
      toast.success('Alimento personalizado eliminado');
      return true;
    } catch (error) {
      console.error('Error in deleteCustomFood:', error);
      toast.error('Error al eliminar el alimento personalizado');
      return false;
    }
  };

  // Cargar alimentos al montar el hook
  useEffect(() => {
    loadCustomFoods();
  }, []);

  return {
    customFoods,
    isLoading,
    loadCustomFoods,
    saveCustomFood,
    deleteCustomFood
  };
};
